import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { semantic } from "../../../theme/tokens";
import { debounce } from "lodash";
import dayjs from "dayjs";
import {
    Drawer,
    Form,
    Input,
    Select,
    DatePicker,
    Switch,
    Button,
    Space,
    Divider,
    Row,
    Col,
    Modal,
    Spin,
    message,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
    customerAPIWithErrorHandling,
    customerHelpers,
} from "../../../api/customer/customer";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";

/** Cây đơn vị -> danh sách phẳng cho Select, id luôn về kiểu số. */
const flattenOrgTree = (nodes = []) => {
    let result = [];
    nodes.forEach((node) => {
        result.push({ value: Number(node.id), label: node.name });
        if (node.children_recursive?.length > 0) {
            result = result.concat(flattenOrgTree(node.children_recursive));
        }
    });
    return result;
};

/**
 * Drawer thêm mới / chỉnh sửa khách hàng.
 *
 * Màn chi tiết khách hàng vẫn là trang riêng (nhiều khối: tài sản, đợt thu,
 * thống kê, lịch sử gọi), chỉ form nhập liệu chuyển vào drawer để giữ nguyên
 * trang và bộ lọc đang bật trên danh sách.
 *
 * Dữ liệu điền sẵn lấy thẳng từ dòng trên bảng: API danh sách đã trả model đầy
 * đủ kèm quan hệ organizations nên không cần gọi lại getCustomerById.
 *
 * @param {boolean} open
 * @param {object|null} record   Có -> chế độ sửa, null -> chế độ thêm mới.
 * @param {object[]} treeData    Danh sách đơn vị đã làm phẳng (trang danh sách nạp sẵn).
 * @param {function} onClose
 * @param {function} onSaved     Gọi sau khi lưu thành công để trang danh sách nạp lại.
 */
export default function CustomerFormDrawer({
    open,
    record = null,
    treeData = [],
    onClose,
    onSaved,
}) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const discountType = Form.useWatch("discount_type", form);
    const isEdit = !!record;

    const { user } = useSelector((state) => state.persistedReducer.user);
    const userType = user?.data?.info?.type;

    // Cán bộ/quản lý chi nhánh không đổi được đơn vị của khách đã có: backend chỉ
    // đồng bộ organization_ids khi người thao tác là SHOP, hiện ô ra chỉ gây hiểu nhầm.
    // Lúc thêm mới thì mọi vai trò đều dùng được nên vẫn hiện.
    const canEditOrganizations =
        !isEdit || (userType !== "STAFF" && userType !== "BRANCH_MANAGER");

    // Nơi gọi nào đã nạp sẵn cây đơn vị thì truyền xuống, nơi nào không có (màn chi
    // tiết khách hàng) thì drawer tự nạp khi mở lần đầu.
    const [ownOrgOptions, setOwnOrgOptions] = useState([]);

    // Trang danh sách dựng treeData với value là chuỗi, còn organizations của khách
    // trả về id kiểu số. Không quy về một kiểu thì ô chọn hiện id trần thay vì tên đơn vị.
    const orgOptions = useMemo(
        () =>
            treeData.length > 0
                ? treeData.map((node) => ({
                    value: Number(node.value ?? node.id),
                    label: node.title ?? node.label,
                }))
                : ownOrgOptions,
        [treeData, ownOrgOptions]
    );

    useEffect(() => {
        if (!open || !canEditOrganizations) return;
        if (treeData.length > 0 || ownOrgOptions.length > 0) return;

        const loadOrganizations = async () => {
            try {
                const response = await organizationAPIWithErrorHandling.getTreeStructure();
                if (response.success) setOwnOrgOptions(flattenOrgTree(response.data));
            } catch (error) {
                message.error(error.message || "Không thể tải danh sách đơn vị");
            }
        };

        loadOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, canEditOrganizations]);

    // Kiểm tra trùng số điện thoại
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
    const [existingCustomer, setExistingCustomer] = useState(null);

    // Số hiện tại của bản ghi đang sửa, giữ trong ref để hàm debounce không phải
    // dựng lại mỗi lần đổi bản ghi (dựng lại là mất luôn lần gõ đang chờ).
    const currentPhoneRef = useRef(null);

    const debouncedCheckPhone = useMemo(
        () =>
            debounce(async (phone) => {
                if (!phone) return;

                const rawPhone = phone.replace(/\D/g, "");
                // Sửa mà số không đổi thì chính nó là bản trùng, không việc gì phải hỏi
                if (rawPhone === currentPhoneRef.current) return;
                if (rawPhone.length < 9 || rawPhone.length > 12) return;

                setCheckingPhone(true);
                try {
                    const response = await customerAPIWithErrorHandling.checkPhone(phone);
                    if (response.data?.success && response.data.data) {
                        setExistingCustomer(response.data.data);
                        setDuplicateModalVisible(true);
                    }
                } catch (error) {
                    console.error("Error checking phone:", error);
                } finally {
                    setCheckingPhone(false);
                }
            }, 300),
        []
    );

    useEffect(() => () => debouncedCheckPhone.cancel(), [debouncedCheckPhone]);

    useEffect(() => {
        if (!open) return;

        setDuplicateModalVisible(false);
        setExistingCustomer(null);

        if (isEdit) {
            currentPhoneRef.current = record.phone?.replace(/\D/g, "") ?? null;

            form.setFieldsValue({
                name: record.name,
                phone: record.phone,
                dob: record.dob ? dayjs(record.dob) : null,
                email: record.email,
                organization_ids: (record.organizations || []).map((org) => Number(org.id)),
                discount_type: record.discount_type,
                discount_value: record.discount_value,
                identity_account: record.identity_account,
                status: !!record.status,
            });
        } else {
            currentPhoneRef.current = null;
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);

    const handleConfirmMapping = () => {
        if (existingCustomer) {
            form.setFieldsValue({
                name: existingCustomer.name,
                email: existingCustomer.email,
                identity_account: existingCustomer.identity_account,
                dob: existingCustomer.dob ? dayjs(existingCustomer.dob) : null,
            });
            message.info(
                isEdit
                    ? "Đã tự động điền thông tin từ khách hàng có sẵn."
                    : "Đã tự động điền thông tin từ khách hàng có sẵn. Bấm Thêm mới để hoàn tất gán đơn vị."
            );
        }
        setDuplicateModalVisible(false);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const customerData = customerHelpers.formatCustomerData({
                ...values,
                dob: values.dob ? values.dob.format?.("YYYY-MM-DD") || values.dob : null,
            });

            const validation = customerHelpers.validateCustomerData(customerData);
            if (!validation.isValid) {
                Object.keys(validation.errors).forEach((field) => {
                    form.setFields([{ name: field, errors: [validation.errors[field]] }]);
                });
                return;
            }

            if (isEdit) {
                await customerAPIWithErrorHandling.updateCustomer(record.id, customerData);
                message.success("Cập nhật thông tin khách hàng thành công!");
            } else {
                const response = await customerAPIWithErrorHandling.createCustomer(customerData);

                // Số điện thoại đã có trên hệ thống -> backend gán khách cũ vào đơn vị
                // của mình thay vì tạo bản ghi mới, nói rõ để người nhập không tưởng nhầm.
                if (response.data?.data?.is_existing) {
                    message.success("Khách hàng đã tồn tại và đã được gán vào đơn vị mới thành công!");
                } else {
                    message.success("Thêm khách hàng thành công!");
                }
            }

            onSaved?.();
            onClose?.();
        } catch (error) {
            // Drawer hẹp, toast dễ bị bỏ qua nên lỗi theo trường phải hiện ngay tại ô.
            if (error.fieldErrors) {
                form.setFields(
                    Object.entries(error.fieldErrors).map(([name, errors]) => ({
                        name,
                        errors: Array.isArray(errors) ? errors : [errors],
                    }))
                );
                message.error("Dữ liệu chưa hợp lệ, kiểm tra lại các ô được đánh dấu.");
            } else {
                message.error(
                    error.message ||
                    (isEdit ? "Không thể cập nhật khách hàng" : "Không thể thêm khách hàng")
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Chỉnh sửa khách hàng" : "Thêm khách hàng"}
            width="min(720px, 100vw)"
            open={open}
            onClose={onClose}
            destroyOnHidden
            maskClosable={!submitting}
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                        {isEdit ? "Lưu thay đổi" : "Thêm mới"}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark
                initialValues={{ status: true }}
            >
                <Form.Item
                    name="name"
                    label="Họ và tên"
                    rules={[
                        { required: true, message: "Vui lòng nhập họ và tên!" },
                        { min: 2, message: "Họ tên phải có ít nhất 2 ký tự!" },
                        { max: 100, message: "Họ tên không được vượt quá 100 ký tự!" },
                    ]}
                >
                    <Input placeholder="Nhập họ và tên đầy đủ" />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="phone"
                            label="Số điện thoại (ưu tiên Zalo)"
                            rules={[
                                { required: true, message: "Vui lòng nhập số điện thoại!" },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const rawPhone = value.replace(/\D/g, "");
                                        if (rawPhone.length < 9 || rawPhone.length > 12) {
                                            return Promise.reject("Số điện thoại phải từ 9 đến 12 chữ số!");
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder="0912345678"
                                maxLength={15}
                                onChange={(e) => debouncedCheckPhone(e.target.value)}
                                suffix={checkingPhone ? <Spin size="small" /> : null}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="dob"
                            label="Ngày sinh"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value && new Date(value) > new Date()) {
                                            return Promise.reject("Ngày sinh không thể lớn hơn ngày hiện tại!");
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <DatePicker
                                placeholder="Chọn ngày sinh"
                                format="DD/MM/YYYY"
                                style={{ width: "100%" }}
                                disabledDate={(current) => current && new Date(current) > new Date()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: "email", message: "Email không hợp lệ!" }]}
                >
                    <Input placeholder="example@email.com" />
                </Form.Item>

                {canEditOrganizations && (
                    <Form.Item name="organization_ids" label="Thuộc đơn vị">
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            style={{ width: "100%" }}
                            placeholder="Chọn đơn vị"
                            options={orgOptions}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                )}

                <Divider orientation="left" plain style={{ marginTop: 8 }}>
                    Thông tin bổ sung
                </Divider>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="discount_type" label="Loại giảm trừ">
                            <Select
                                placeholder="Chọn loại giảm trừ"
                                allowClear
                                options={[
                                    { value: "PERCENTAGE", label: "Phần trăm (%)" },
                                    { value: "FIXED", label: "Cố định (₫)" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="discount_value" label="Giá trị">
                            <Input
                                type="number"
                                placeholder="Nhập giá trị giảm trừ"
                                disabled={!discountType}
                                addonAfter={
                                    discountType === "PERCENTAGE"
                                        ? "%"
                                        : discountType === "FIXED"
                                            ? "₫"
                                            : ""
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} sm={14}>
                        <Form.Item name="identity_account" label="Số định danh">
                            <Input placeholder="Nhập thông tin định danh" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                        <Form.Item name="status" label="Trạng thái tài khoản" valuePropName="checked">
                            <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Modal
                title={
                    <span style={{ color: semantic.warning }}>
                        <UserOutlined style={{ marginRight: 8 }} />
                        Khách hàng đã tồn tại trên hệ thống
                    </span>
                }
                open={duplicateModalVisible}
                onOk={handleConfirmMapping}
                onCancel={() => setDuplicateModalVisible(false)}
                okText={isEdit ? "Sử dụng thông tin & Cập nhật" : "Sử dụng thông tin & Thêm vào hệ thống"}
                cancelText="Bỏ qua"
                centered
                width={500}
            >
                <div className="py-4">
                    <p className="mb-4 text-gray-600">
                        Số điện thoại <strong>{existingCustomer?.phone}</strong> đã được đăng ký bởi:
                    </p>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 mb-4">
                        <p className="mb-1"><strong>Họ và tên:</strong> {existingCustomer?.name}</p>
                        <p className="mb-1"><strong>Email:</strong> {existingCustomer?.email || "N/A"}</p>
                        <p className="mb-0">
                            <strong>Đang thuộc đơn vị:</strong>{" "}
                            {existingCustomer?.organizations?.length > 0
                                ? existingCustomer.organizations.map((o) => o.name).join(", ")
                                : "Chưa gán đơn vị"}
                        </p>
                    </div>
                    <p className="text-sm text-gray-500 italic">
                        * Bạn có muốn lấy thông tin của khách hàng này để thêm vào quản lý của mình không?
                    </p>
                </div>
            </Modal>
        </Drawer>
    );
}
